import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import {
  Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2, AlertCircle,
} from 'lucide-react';

// Use unpkg (mirrors npm directly so the version always matches pdfjs-dist).
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MIN_SCALE = 0.5;
const MAX_SCALE = 2.5;
const SCALE_STEP = 0.2;

/**
 * Reusable authenticated PDF preview dialog.
 *
 * Props:
 *   open          — boolean
 *   onClose       — () => void
 *   pdfUrl        — full URL to the authenticated PDF endpoint
 *   fetchHeaders  — object, e.g. { Authorization: 'Bearer xxx' }
 *   title         — string shown in the dialog header
 *   downloadName  — filename for the Download button (default 'preview.pdf')
 */
export default function PDFPreviewDialog({
  open, onClose, pdfUrl, fetchHeaders, title, downloadName = 'preview.pdf',
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // Fetch the PDF whenever the dialog opens for a new URL.
  useEffect(() => {
    if (!open || !pdfUrl) return undefined;
    let cancelled = false;
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);
    setNumPages(0);
    setPageNumber(1);
    setScale(1.0);

    (async () => {
      try {
        const resp = await fetch(pdfUrl, { headers: fetchHeaders || {}, signal: ctrl.signal });
        if (!resp.ok) throw new Error(`Server returned ${resp.status}`);
        const ctype = resp.headers.get('content-type') || '';
        const buf = await resp.arrayBuffer();
        if (cancelled) return;
        if (!ctype.toLowerCase().includes('pdf') && buf.byteLength < 8) {
          throw new Error('Response is not a valid PDF');
        }
        const blob = new Blob([buf], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (e) {
        if (cancelled || e.name === 'AbortError') return;
        setError(e.message || 'Failed to load PDF');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [open, pdfUrl, fetchHeaders]);

  // Revoke blob URL on close or unmount to prevent memory leaks.
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // When dialog closes, drop the blob URL eagerly.
  useEffect(() => {
    if (!open && blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
  }, [open, blobUrl]);

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [blobUrl, downloadName]);

  const onDocLoad = useCallback(({ numPages: n }) => setNumPages(n), []);
  const onDocError = useCallback((err) => setError(err?.message || 'Failed to render PDF'), []);

  const goPrev = () => setPageNumber((p) => Math.max(1, p - 1));
  const goNext = () => setPageNumber((p) => Math.min(numPages || 1, p + 1));
  const zoomIn = () => setScale((s) => Math.min(MAX_SCALE, +(s + SCALE_STEP).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(MIN_SCALE, +(s - SCALE_STEP).toFixed(2)));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        data-testid="pdf-preview-dialog"
        className="max-w-4xl w-[90vw] p-0 bg-[#0d0d0d] border border-[#2a2a2a] text-zinc-100"
      >
        <DialogHeader className="px-4 py-3 border-b border-[#2a2a2a] flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-sm font-semibold tracking-tight text-zinc-100">
            {title || 'PDF Preview'}
          </DialogTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              data-testid="pdf-preview-download-btn"
              disabled={!blobUrl || loading || !!error}
              onClick={handleDownload}
              className="h-8 text-xs border-[#2a2a2a] bg-transparent text-zinc-200 hover:bg-zinc-800"
            >
              <Download className="h-3.5 w-3.5 mr-1" /> Download
            </Button>
          </div>
        </DialogHeader>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-[#1f1f1f] bg-[#0a0a0a]">
          <div className="flex items-center gap-1.5">
            <Button
              size="sm" variant="ghost" disabled={pageNumber <= 1 || !numPages}
              onClick={goPrev} data-testid="pdf-preview-prev-btn"
              className="h-7 w-7 p-0 text-zinc-300 hover:text-amber-400"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-zinc-400 tabular-nums min-w-[64px] text-center" data-testid="pdf-preview-page-counter">
              {numPages ? `${pageNumber} / ${numPages}` : '— / —'}
            </span>
            <Button
              size="sm" variant="ghost" disabled={!numPages || pageNumber >= numPages}
              onClick={goNext} data-testid="pdf-preview-next-btn"
              className="h-7 w-7 p-0 text-zinc-300 hover:text-amber-400"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm" variant="ghost" disabled={scale <= MIN_SCALE || !numPages}
              onClick={zoomOut} data-testid="pdf-preview-zoom-out-btn"
              className="h-7 w-7 p-0 text-zinc-300 hover:text-amber-400"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-xs text-zinc-400 tabular-nums min-w-[40px] text-center">
              {Math.round(scale * 100)}%
            </span>
            <Button
              size="sm" variant="ghost" disabled={scale >= MAX_SCALE || !numPages}
              onClick={zoomIn} data-testid="pdf-preview-zoom-in-btn"
              className="h-7 w-7 p-0 text-zinc-300 hover:text-amber-400"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div
          className="flex items-start justify-center overflow-auto bg-[#1a1a1a]"
          style={{ minHeight: 480, maxHeight: '70vh' }}
        >
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 text-zinc-400" data-testid="pdf-preview-loading">
              <Loader2 className="h-6 w-6 animate-spin mb-3" />
              <p className="text-xs">Loading PDF…</p>
            </div>
          )}
          {error && !loading && (
            <div className="flex flex-col items-center justify-center py-16 text-zinc-400 max-w-md text-center" data-testid="pdf-preview-error">
              <AlertCircle className="h-6 w-6 mb-3 text-red-400" />
              <p className="text-sm text-red-300 font-medium">Could not load preview</p>
              <p className="text-xs mt-1 text-zinc-500">{error}</p>
            </div>
          )}
          {!loading && !error && blobUrl && (
            <Document
              file={blobUrl}
              onLoadSuccess={onDocLoad}
              onLoadError={onDocError}
              loading={<div className="py-16 text-zinc-400 text-xs">Rendering…</div>}
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className="my-4 shadow-lg"
              />
            </Document>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
